const { useEffect, useState, useRef } = React;

function Advantages() {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.2 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (isVisible && typeof lucide !== "undefined" && lucide.createIcons) {
            lucide.createIcons();
        }
    }, [isVisible]);

    const advantages = [
        {
            icon: "badge-check",
            title: "Verified Listings",
            description:
                "Every property is reviewed to help keep listings accurate and trustworthy.",
        },
        {
            icon: "zap",
            title: "Seamless Booking",
            description:
                "Schedule viewings in seconds without back-and-forth calls or long wait times.",
        },
        {
            icon: "clock",
            title: "Real-Time Availability",
            description:
                "See availability updates instantly and book slots that fit your schedule.",
        },
        {
            icon: "filter",
            title: "Smart Filters",
            description:
                "Filter by price, location, property type, and amenities to find the right fit.",
        },
        {
            icon: "sparkles",
            title: "Modern Experience",
            description:
                "A clean, responsive interface designed to feel fast and effortless.",
        },
        {
            icon: "lock",
            title: "Secure Platform",
            description:
                "Protected accounts and role-based access for clients and realtors.",
        },
    ];

    return (
        <section
            id="advantages"
            ref={sectionRef}
            className="section-shell relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden"
        >
            {/* Decorative Background */}
            <div className="absolute inset-0">
                <div className="absolute top-20 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
                <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
            </div>

            <div className="max-w-6xl mx-auto relative z-10">
                {/* Section Header */}
                <div
                    className={`text-center mb-16 transition-all duration-1000 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    }`}
                >
                    <div className="section-kicker mx-auto mb-4 w-fit">Benefits</div>
                    <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900 section-title">
                        Why Choose{" "}
                        <span className="bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                            BEASMAR
                        </span>
                    </h2>
                    <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                        Practical features built for fast discovery, smooth booking, and trusted
                        realtor collaboration.
                    </p>
                    <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-8"></div>
                </div>

                {/* Advantages Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {advantages.map((advantage, index) => (
                        <div
                            key={advantage.title}
                            className={`group transition-all duration-1000 ${
                                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                            }`}
                            style={{ transitionDelay: `${index * 90}ms` }}
                        >
                            <div className="relative h-full">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-blue-400/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                                <div className="relative h-full surface-card p-7 transition-all duration-500 group-hover:shadow-xl group-hover:shadow-blue-500/10">
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600/18 to-blue-400/10 flex items-center justify-center border border-blue-300/30">
                                            <i
                                                data-lucide={advantage.icon}
                                                className="w-6 h-6 text-blue-600"
                                            ></i>
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {advantage.title}
                                        </h3>
                                    </div>

                                    <p className="text-gray-600 leading-relaxed">{advantage.description}</p>

                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-b-2xl opacity-70"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default Advantages;

